import { MediaPicker } from "../../components/MediaPicker";
import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, X, Award } from "lucide-react";
import { WorkflowPanel } from "./AdminReview";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "", short_description: "", long_description: "", category: "energy",
  image_url: "", target_amount: "Target: To be confirmed", progress_percent: 0,
  is_active: true, order: 0, raised_amount: 0,
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/projects?active_only=false");
    setItems(r.data);
  };
  useEffect(() => { document.title = "Admin · Projects"; load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        await api.put(`/admin/projects/${editing.id}`, editing);
        toast.success("Project updated");
      } else {
        await api.post("/admin/projects", editing);
        toast.success("Project created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const issueCerts = async (p) => {
    try {
      const { data } = await api.post(`/admin/projects/${p.id}/issue-certificates`);
      toast.success(`${data.issued} new certificate(s) issued · ${data.total_for_project} total for ${p.serial || p.title}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl text-[#0a0d12] font-bold">Projects</h1>
          <p className="text-[#0a0d12]/60 text-sm">Add, edit and remove projects shown on the public site.</p>
        </div>
        <button onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="dk-btn-primary !py-2.5 !px-4 text-sm" data-testid="add-project-btn">
          <Plus size={16} /> Add project
        </button>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#f8f4e8] text-left">
            <tr><th className="p-3">Title</th><th className="p-3">Category</th><th className="p-3">Progress</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-black/5">
                <td className="p-3 font-semibold text-[#0a0d12]">{p.title}</td>
                <td className="p-3 capitalize">{p.category}</td>
                <td className="p-3">{p.progress_percent}%</td>
                <td className="p-3">{p.is_active ? <span className="text-green-700">Active</span> : <span className="text-[#0a0d12]/40">Inactive</span>}<div className="text-[10px] capitalize text-[#0a0d12]/50">{(p.status || "").replace(/_/g, " ")}</div></td>
                <td className="p-3 text-right whitespace-nowrap">
                  {p.status === "completed" && (
                    <button onClick={() => issueCerts(p)} title="Issue impact certificates" className="p-1.5 hover:bg-[#f8f4e8] rounded mr-1 text-[#d6aa43]" data-testid={`certs-${p.id}`}><Award size={14} /></button>
                  )}
                  <button onClick={() => { setEditing(p); setOpen(true); }} className="p-1.5 hover:bg-[#f8f4e8] rounded mr-1" data-testid={`edit-${p.id}`}><Edit2 size={14} /></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`del-${p.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[#0a0d12]/50">No projects yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-7 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="project-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl font-bold">{editing.id ? "Edit project" : "Add project"}</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>
            {editing.id && <div className="mb-5"><WorkflowPanel collection="projects" recordId={editing.id} onChanged={load} /></div>}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field2 label="Title" required value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <Field2 label="Category" required value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} placeholder="energy, education, ..." />
              <div className="sm:col-span-2 flex items-end gap-2"><div className="flex-1"><Field2 label="Image URL" required value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} /></div><MediaPicker value={editing.image_url} onChange={(u) => setEditing({ ...editing, image_url: u })} testid="project-image-upload" /></div>
              <Field2 label="Short description" required value={editing.short_description} onChange={(v) => setEditing({ ...editing, short_description: v })} className="sm:col-span-2" textarea />
              <Field2 label="Long description" value={editing.long_description} onChange={(v) => setEditing({ ...editing, long_description: v })} className="sm:col-span-2" textarea />
              <Field2 label="Target label" value={editing.target_amount} onChange={(v) => setEditing({ ...editing, target_amount: v })} />
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Status</label>
                <select value={editing.status || "active"} onChange={(v) => setEditing({ ...editing, status: v.target.value })} className="w-full px-4 py-2.5 border border-black/15 rounded-md bg-white" data-testid="project-status-select">
                  {["proposed", "fundraising", "active", "funded", "in_progress", "completed", "monitoring"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <p className="text-[10px] text-[#0a0d12]/50 mt-1">Setting Completed emails an Impact Certificate to every donor of this project.</p>
              </div>
              <Field2 label="Location (Community, LGA, State)" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} className="sm:col-span-2" placeholder="e.g. Jimeta, Yola North, Adamawa" />
              <Field2 label="Latitude (optional)" type="number" value={editing.lat ?? ""} onChange={(v) => setEditing({ ...editing, lat: v === "" ? null : Number(v) })} />
              <Field2 label="Longitude (optional)" type="number" value={editing.lng ?? ""} onChange={(v) => setEditing({ ...editing, lng: v === "" ? null : Number(v) })} />
              <Field2 label="Beneficiaries estimate" type="number" value={editing.beneficiaries_estimate} onChange={(v) => setEditing({ ...editing, beneficiaries_estimate: Number(v) })} />
              <Field2 label="Progress (%)" type="number" value={editing.progress_percent} onChange={(v) => setEditing({ ...editing, progress_percent: Number(v) })} />
              <Field2 label="Order" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: Number(v) })} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Active
              </label>
              <label className="flex items-center gap-2 text-sm" title="Untick while the budget is still under community/technical verification. Unticked projects show 'Under verification' and cannot receive project-specific donations.">
                <input type="checkbox" checked={editing.target_confirmed !== false} onChange={(e) => setEditing({ ...editing, target_confirmed: e.target.checked })} data-testid="project-target-confirmed" /> Budget verified &amp; approved (opens project for funding)
              </label>
              <div className="sm:col-span-2 mt-2" data-testid="evidence-editor">
                <div className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-2">Evidence badges — set the date when each check was completed (leave blank to derive from stage)</div>
                <div className="space-y-2">
                  {[["need_verified", "Need Verified", "Programme team"], ["budget_approved", "Budget Approved", "Programme Director & Finance"], ["funding_verified", "Funding Verified", "Finance"], ["implementation_evidence", "Implementation Evidence", "Field team"], ["completion_verified", "Completion Verified", "Monitoring & Evaluation"], ["post_monitoring", "Post-project Monitoring", "Monitoring & Evaluation"]].map(([k, l, role]) => {
                    const ev = (editing.evidence || {})[k] || {};
                    const setEv = (patch) => setEditing({ ...editing, evidence: { ...(editing.evidence || {}), [k]: { ...ev, ...patch } } });
                    return (
                      <div key={k} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_1fr_1fr] gap-2 items-center p-2 rounded-md bg-[#f8f4e8] border border-black/5 text-sm">
                        <div className="font-semibold">{l}</div>
                        <input type="date" value={ev.date || ""} onChange={(e) => setEv({ date: e.target.value })} className="px-2 py-1.5 border border-black/15 rounded-md bg-white text-xs" data-testid={`evidence-date-${k}`} />
                        <input placeholder={`Verified by (role) — default ${role}`} value={ev.by_role || ""} onChange={(e) => setEv({ by_role: e.target.value })} className="px-2 py-1.5 border border-black/15 rounded-md bg-white text-xs" />
                        <input placeholder="Public note (optional)" value={ev.note || ""} onChange={(e) => setEv({ note: e.target.value })} className="px-2 py-1.5 border border-black/15 rounded-md bg-white text-xs" />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <button type="submit" className="dk-btn-primary mt-6" data-testid="save-project">{editing.id ? "Save changes" : "Create project"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

function Field2({ label, value, onChange, type = "text", textarea, className = "", required, placeholder }) {
  const cls = "w-full px-4 py-2.5 border border-black/15 rounded-md focus:outline-none focus:border-[#d6aa43]";
  const tid = `project-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`;
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">{label}{required ? " *" : ""}</label>
      {textarea
        ? <textarea data-testid={tid} required={required} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} placeholder={placeholder} />
        : <input data-testid={tid} required={required} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} placeholder={placeholder} />}
    </div>
  );
}
